import { renderWidget } from './widget';
import type { WidgetOptions } from './widget';

interface InitOptions {
    apiBaseUrl?: string;
    apiKey?: string;
    region?: number;
    target?: HTMLElement | string;
}

const DEFAULT_REGION = 5;

const hostScript = typeof document !== 'undefined'
    ? (document.currentScript as HTMLScriptElement | null)
    : null;

function resolveContainer(script: HTMLScriptElement, target?: HTMLElement | string): HTMLElement {
    if (typeof target === 'string') {
        const element = document.querySelector(target);
        if (element instanceof HTMLElement) {
            return element;
        }
    }

    if (target instanceof HTMLElement) {
        return target;
    }

    const placeholder = document.createElement('div');
    placeholder.className = 'strompris-widget-host';
    script.parentNode?.insertBefore(placeholder, script);
    return placeholder;
}

function resolveDefaultApiBase(script: HTMLScriptElement): string {
    if (typeof window !== 'undefined') {
        try {
            const src = script.getAttribute('src') ?? script.src ?? '';
            if (src) {
                const resolved = new URL(src, window.location.href);
                return `${resolved.origin}/api`;
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Kunne ikke avlede API-base fra script-tag', error);
        }
    }
    return 'https://api.strompriser.no';
}

function parseDataset(script: HTMLScriptElement): InitOptions {
    const { apiKey, apiBase, region, target } = script.dataset;
    const parsedRegion = region ? Number.parseInt(region, 10) : undefined;

    const parsed: InitOptions = {};

    if (apiKey) {
        parsed.apiKey = apiKey;
    }

    if (apiBase) {
        parsed.apiBaseUrl = apiBase;
    }

    if (Number.isFinite(parsedRegion)) {
        parsed.region = parsedRegion as number;
    }

    if (target) {
        parsed.target = target;
    }

    return parsed;
}

function normalizeApiBase(raw: string | undefined, fallback: string, script: HTMLScriptElement): string {
    if (!raw) {
        return fallback;
    }

    const trimmed = raw.trim();
    if (trimmed.length === 0) {
        return fallback;
    }

    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }

    if (typeof window !== 'undefined') {
        try {
            if (trimmed.startsWith('/')) {
                return new URL(trimmed, window.location.origin).toString();
            }

            const scriptSrc = script.getAttribute('src') ?? script.src ?? window.location.href;
            return new URL(trimmed, scriptSrc).toString();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Kunne ikke tolke API-base fra attributt', error);
        }
    }

    return fallback;
}

async function bootstrap(options: InitOptions = {}, scriptElement?: HTMLScriptElement | null): Promise<void> {
    const script = scriptElement ?? hostScript ?? (document.currentScript as HTMLScriptElement | null);
    if (!script) {
        return;
    }

    const datasetOptions = parseDataset(script);
    const defaultApiBase = resolveDefaultApiBase(script);
    const apiBaseUrl = normalizeApiBase(options.apiBaseUrl ?? datasetOptions.apiBaseUrl, defaultApiBase, script);
    const region = options.region ?? datasetOptions.region ?? DEFAULT_REGION;
    const target = options.target ?? datasetOptions.target;
    const apiKey = options.apiKey ?? datasetOptions.apiKey;

    const container = resolveContainer(script, target);

    const widgetOptions: WidgetOptions = {
        apiBaseUrl,
        region,
        container,
        ...(apiKey ? { apiKey } : {}),
    };

    await renderWidget(widgetOptions);
}

void bootstrap({}, hostScript);

declare global {
    interface Window {
        StromprisWidget?: {
            init: (options?: InitOptions) => Promise<void>;
        };
    }
}

if (typeof window !== 'undefined') {
    window.StromprisWidget = {
        init: (options?: InitOptions) => bootstrap(options, hostScript),
    };
}

